import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { propertyAPI } from '../services/api';
import Hero from '../components/Hero';
import PropertyGallery from '../components/PropertyGallery';
import ROICalculator from '../components/ROICalculator';
import ContactForm from '../components/ContactForm';
import InteractiveMap from '../components/InteractiveMap';
import ExposeSections from '../components/ExposeSections';
import TransactionTimeline from '../components/TransactionTimeline';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Building2, MapPin, Bed, Utensils, Users, Home, ChevronRight, Download, Mail, Phone, ArrowUp, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const iconMap = {
  'map-pin': MapPin,
  'bed': Bed,
  'utensils': Utensils,
  'users': Users,
  'home': Home,
  'building-2': Building2
};

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [propertyData, setPropertyData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Get current language
        const currentLang = i18n.language || 'de';
        
        const [info, highlights, details, expose, transactionProcess] = await Promise.all([
          propertyAPI.getInfo(),
          propertyAPI.getHighlights(),
          propertyAPI.getDetails(currentLang),
          propertyAPI.getExpose(currentLang),
          propertyAPI.getTransactionProcess(currentLang)
        ]);
        
        setPropertyData({
          hero: info.hero,
          quickFacts: info.quickFacts,
          investmentHighlights: highlights.highlights,
          hotel: details.hotel,
          villa: details.villa,
          restaurant: details.restaurant,
          expose: expose,
          transactionProcess: transactionProcess.steps,
          contact: {
            email: "d.boettche@webvision-nord.de",
            exposeUrl: "/expose.pdf"
          }
        });
      } catch (error) {
        console.error('Error loading property data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [i18n.language]); // Reload when language changes

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#C9A961] mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">{t('common.loadingProperty')}</p>
        </div>
      </div>
    );
  }

  if (!propertyData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-slate-600">{t('common.loadingError')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Left */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C9A961] to-[#D4AF37] flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-extrabold text-[#0A2540] tracking-tight">Seeschloss-Hotel</h1>
                <p className="text-xs text-[#C9A961] font-semibold">Kellersee</p>
              </div>
            </div>
            
            {/* Navigation - Center/Right */}
            <nav className="hidden md:flex items-center space-x-4">
              <a href="#overview" className="text-sm font-semibold text-slate-700 hover:text-[#C9A961] transition-colors">{t('nav.overview')}</a>
              <a href="#properties" className="text-sm font-semibold text-slate-700 hover:text-[#C9A961] transition-colors">{t('nav.highlights')}</a>
              <a href="#expose" className="text-sm font-semibold text-slate-700 hover:text-[#C9A961] transition-colors">Exposé</a>
              <a href="/restaurant" className="text-sm font-semibold text-slate-700 hover:text-[#C9A961] transition-colors">Restaurant</a>
              <a href="#calculator" className="text-sm font-semibold text-slate-700 hover:text-[#C9A961] transition-colors">{t('nav.calculator')}</a>
              <LanguageSwitcher />
              <Button 
                onClick={scrollToContact} 
                className="bg-gradient-to-r from-[#C9A961] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E5C158] text-white font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm px-4 py-2"
              >
                {t('nav.contact')}
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <Hero data={propertyData.hero} onContactClick={scrollToContact} />

      {/* Quick Facts */}
      <section className="py-20 bg-white border-b border-slate-200" id="overview">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {t('quickFacts', { returnObjects: true }).map((fact, idx) => {
              const icons = ['MapPin', 'Bed', 'Users', 'Home'];
              const iconNames = ['map-pin', 'bed', 'users', 'home'];
              const Icon = iconMap[iconNames[idx]];
              return (
                <Card key={idx} className="group border-2 border-slate-200 hover:border-[#C9A961] transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 bg-gradient-to-br from-white to-slate-50">
                  <CardContent className="p-6 md:p-8 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-[#C9A961] to-[#D4AF37] flex items-center justify-center shadow-lg shadow-[#C9A961]/30 group-hover:scale-110 transition-transform duration-300">
                        {Icon && <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />}
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-slate-600 mb-2 uppercase tracking-wide">{fact.label}</p>
                    <p className="text-2xl md:text-3xl font-extrabold text-[#0A2540] mb-2">{fact.value}</p>
                    <p className="text-xs text-slate-500 font-medium">{fact.subtext}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Highlights */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,169,97,0.05),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(10,37,64,0.03),transparent_50%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('highlights.badge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('highlights.title')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('highlights.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t('investmentHighlights', { returnObjects: true }).map((highlight, idx) => (
              <Card key={idx} className="group border-2 border-slate-200 hover:border-[#C9A961] hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white/80 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#C9A961]/10 to-[#D4AF37]/10 rounded-bl-full transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-8 relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C9A961] to-[#D4AF37] flex items-center justify-center mb-6 shadow-xl shadow-[#C9A961]/30 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl text-white font-extrabold">{idx + 1}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A2540] mb-4 group-hover:text-[#C9A961] transition-colors duration-300">{highlight.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-base">{highlight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Property Sections */}
      <section className="py-24 bg-white" id="properties">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <Badge className="mb-6 bg-gradient-to-r from-[#2C5F66] to-[#0A2540] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('sections.propertiesBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('properties.title')}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full"></div>
          </div>

          {/* Hotel */}
          <div className="mb-20">
            <PropertyGallery 
              title={t('properties.hotel.title')}
              description={t('properties.hotel.description')}
              features={t('properties.hotel.features', { returnObjects: true })}
              images={propertyData.hotel.images}
              theme="hotel"
            />
          </div>

          {/* Villa */}
          <div className="mb-20">
            <PropertyGallery 
              title={t('properties.villa.title')}
              description={t('properties.villa.description')}
              features={t('properties.villa.features', { returnObjects: true })}
              images={propertyData.villa.images}
              theme="villa"
            />
          </div>

          {/* Restaurant section removed - no longer part of property offering */}
        </div>
      </section>

      {/* Exposé Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white" id="expose">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('expose.badge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('expose.title')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('expose.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <ExposeSections data={propertyData.expose} />
          <div className="text-center mt-12">
            <div className="flex flex-col gap-4 items-center">
              {/* German PDF */}
              <Button 
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/property/expose-pdf`, '_blank')}
                className="bg-gradient-to-r from-[#0A2540] to-[#2C5F66] hover:from-[#1E3A5F] hover:to-[#3A7080] text-white px-10 py-7 text-lg font-bold shadow-2xl hover:shadow-[#0A2540]/40 transition-all duration-300 hover:scale-105 rounded-xl w-full max-w-xl"
              >
                <Download className="w-6 h-6 mr-3" />
                {t('expose.downloadDE')}
              </Button>
              
              {/* English PDF */}
              <Button 
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/property/expose-pdf-en`, '_blank')}
                className="bg-gradient-to-r from-[#C9A961] to-[#D4AF37] hover:from-[#B89851] hover:to-[#C49E27] text-[#0A2540] px-10 py-6 text-base font-semibold shadow-xl hover:shadow-[#C9A961]/40 transition-all duration-300 hover:scale-105 rounded-xl w-full max-w-xl"
              >
                <Download className="w-5 h-5 mr-3" />
                {t('expose.downloadEN')}
              </Button>
              
              {/* Dutch PDF */}
              <Button 
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/property/expose-pdf-nl`, '_blank')}
                className="bg-gradient-to-r from-[#C9A961] to-[#D4AF37] hover:from-[#B89851] hover:to-[#C49E27] text-[#0A2540] px-10 py-6 text-base font-semibold shadow-xl hover:shadow-[#C9A961]/40 transition-all duration-300 hover:scale-105 rounded-xl w-full max-w-xl"
              >
                <Download className="w-5 h-5 mr-3" />
                {t('expose.downloadNL')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-24 bg-white" id="calculator">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#2C5F66] to-[#0A2540] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('calculator.badge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('calculator.title')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('calculator.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <ROICalculator />
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('sections.locationBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('sections.locationTitle')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('sections.locationSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <InteractiveMap />
        </div>
      </section>

      {/* Transaction Timeline */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#2C5F66] to-[#0A2540] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('sections.timelineBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('sections.timelineTitle')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('sections.timelineSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <TransactionTimeline data={propertyData.transactionProcess} />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-br from-[#0A2540] via-[#1E3A5F] to-[#2C5F66] text-white relative overflow-hidden" id="contact">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,169,97,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,175,55,0.1),transparent_50%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">{t('sections.contactTitle')}</h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              {t('sections.contactSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="w-6 h-6" />
                <span className="font-bold text-lg">Seeschloss-Hotel</span>
              </div>
              <p className="text-slate-300 text-sm">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.contactTitle')}</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>d.boettche@webvision-nord.de</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.legalTitle')}</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <p>{t('footer.copyright')}</p>
                <p>
                  <a href="/impressum" className="hover:text-[#C9A961] transition-colors">
                    Impressum
                  </a>
                  {' '} | {' '}
                  <a href="/datenschutz" className="hover:text-[#C9A961] transition-colors">
                    Datenschutz
                  </a>
                </p>
                <p>{t('footer.disclaimer')}</p>
                <p>{t('footer.bonityRequired')}</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-[#C9A961] text-white rounded-full shadow-lg hover:bg-[#D4AF37] transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Print Button - Always visible */}
      <button
        onClick={handlePrint}
        className="fixed bottom-8 right-24 w-12 h-12 bg-[#0A2540] text-white rounded-full shadow-lg hover:bg-[#0d3052] transition-all duration-300 flex items-center justify-center z-50 hover:scale-110 print:hidden"
        title={t('common.printPage')}
      >
        <Printer className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LandingPage;